import { Component, Input } from '@angular/core';
import { ChipTimeChipPickerLabels } from '@sb/library/base';
import { CalendarBookingDisplayItem, EventColorEnum, EventLocationNameDisplayItem } from '@sb/library/data-models';
import { FormFieldDateBaseComponent } from '@sb/library/form-field';
import { SBTime } from '@sb/library/localization';
import { StyleManager } from '@sb/library/managers';

@Component({
    selector: 'lib-timegrid-picker',
    templateUrl: './timegrid-picker.component.html',
    styleUrl: './timegrid-picker.component.scss',
})
export class TimegridPickerComponent extends FormFieldDateBaseComponent<CalendarBookingDisplayItem | null> {
    @Input()
    set bookingSlots(slots: CalendarBookingDisplayItem[]) {
        this._bookingSlots = slots;
        this.PrepareBookingSlots();
    }

    private readonly _notSpecifiedLocationID = " NotSpecified";

    private _bookingSlots!: CalendarBookingDisplayItem[];
    private _filteredSlots!: CalendarBookingDisplayItem[];
    private _slotSelected!: CalendarBookingDisplayItem | null;
    private _currentTimeRange: (SBTime | null)[] | null = null;
    private _uniqueLocations: EventLocationNameDisplayItem[] = [];
    private _currentLocationSelected: EventLocationNameDisplayItem | null = null;

    private _chipTimeFilterLabels: ChipTimeChipPickerLabels = {
        StartLabel: $localize`:@@TPAttendanceChipTimeFilterStartsAfterLabel:Starts After`,
        StartHelperText: $localize`:@@CommonAnyTime:Any Time`,
        EndHelperText: $localize`:@@CommonAnyTime:Any Time`,
        EndLabel: $localize`:@@TPAttendanceChipTimeFilterStartsBeforeLabel:Starts Before`,
        Title: $localize`:@@TPAttendanceFilterByEventStartTimeTitle:Filter by Event Start Time`,
    };

    constructor(private _StyleManager: StyleManager) {
        super();
    }

    override ngOnInit(): void {
        super.ngOnInit();

        // filter booking slot locations
        const locationsFromSlots = Array.from(
            new Map(this._bookingSlots.map(item => item.EventLocation)
            .map(loc => [loc?.ID, loc])).values()
        );

        // filter out null locations if needed
        this._uniqueLocations = locationsFromSlots.filter((loc): loc is EventLocationNameDisplayItem => loc?.ID != null)

        if (this._uniqueLocations.length) {
            // if there is a not specified location, and there is 1 or more unique locations we need to add it as an option
            if (locationsFromSlots.some(loc => loc === null)) { // if there is a null location, it returns null
                this._uniqueLocations.push(new EventLocationNameDisplayItem({
                    Color: EventColorEnum.LightGrey,
                    ID: this._notSpecifiedLocationID,
                    Name: this.notSpecifiedString
                }));
            }
            this._currentLocationSelected = this._uniqueLocations[0];
        }

        this.PrepareBookingSlots();
    }

    TimeChangeEvent(slot: CalendarBookingDisplayItem | null) {
        if (slot) {
            this.formControl?.setValue(slot);
            this._slotSelected = slot;
        }
    }

    TimeRangeChange(event: (SBTime | null)[] | null): void {
        if (!event) {
            this.FilterBookingSlots();
        } else {
            this._currentTimeRange = event;
            this.FilterBookingSlots(event[0], event[1]);
        } 
    }

    LocationChange(locationID: string): void {
        this._currentLocationSelected = this._uniqueLocations.find(loc => loc.ID == locationID)!;
        this.TriggerFilterChange();
    }

    BackgroundColorClass(color: EventColorEnum): string {
        return this._StyleManager.GetBackgroundColorCssClass(color);
    }

    private TriggerFilterChange(): void {
        if (this._currentTimeRange) {
            this.FilterBookingSlots(this._currentTimeRange[0], this._currentTimeRange[1]);
        } else {
            this.FilterBookingSlots();
        }
    }

    private PrepareBookingSlots(): void {
        this._bookingSlots.sort((a, b) => {
            return a.StartDate.localeCompare(b.StartDate);
        });

        this.TriggerFilterChange();
    }

    private FilterBookingSlots(startRange: SBTime | null = null, endRange: SBTime | null = null) {
        const filteredBookingSlots: CalendarBookingDisplayItem[] = [];

        this._bookingSlots?.forEach(slot => {
            const timeInMs = SBTime.FromISO(slot.StartDate.split('T')[1]).totalMilliseconds;
        
            // these conditions are all the permutations of the outcomes from the chip time range picker
            if (((!startRange && !endRange) || 
                (startRange && !endRange && timeInMs >= startRange.totalMilliseconds) ||
                (!startRange && endRange && timeInMs <= endRange.totalMilliseconds) ||
                (startRange && endRange && timeInMs >= startRange.totalMilliseconds && timeInMs <= endRange.totalMilliseconds))
                // filter by location as well OR ignore if there is no locations
                && (!this._currentLocationSelected ||
                    slot.EventLocationID == this._currentLocationSelected!.ID ||
                    // if not specified is picked, filter slots with EventLocationID null
                    (this._currentLocationSelected.ID == this._notSpecifiedLocationID && !slot.EventLocationID))) {

                filteredBookingSlots.push(slot);
            }
        });

        this._filteredSlots = filteredBookingSlots;

        if (filteredBookingSlots.length) {
            this._slotSelected = filteredBookingSlots[0];
            this.formControl?.setValue(filteredBookingSlots[0]);
        } else {
            this._slotSelected = null;
            this.formControl?.setValue(null);
        }

        this.TimeChangeEvent(this._slotSelected);
    }

    get filteredSlots(): CalendarBookingDisplayItem[] {
        return this._filteredSlots;
    }

    get chipTimeFilterLabels(): ChipTimeChipPickerLabels {
        return this._chipTimeFilterLabels;
    }

    get slotSelected(): CalendarBookingDisplayItem | null {
        return this._slotSelected;
    }

    get uniqueLocations(): EventLocationNameDisplayItem[] {
        return this._uniqueLocations;
    }

    get currentLocationSelected(): EventLocationNameDisplayItem | null {
        return this._currentLocationSelected;
    }
}
